'use client'

import {
  useIsLoggedInQuery,
  userAuthQueryFetcher,
  userAuthQueryKey,
  userAuthQueryOptions,
} from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { userQueryFetcher, userQueryKey, userQueryOptions } from '@repo/cf-user/hooks/auth'
import type { UserQuery } from '@repo/cf-user/gen/user'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

/**
 * Requires a logged-in All Access subscriber before a marketplace action.
 * Opens the login modal when signed out, and the All Access payments dialog
 * (default free-trial plan) when the user is not subscribed.
 *
 * A failed user lookup is treated as not subscribed, same as a missing Studio
 * AI subscription in `useStudioAIGuard`.
 */
export function useAllAccessGuard() {
  const queryClient = useQueryClient()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const loginModal = useUserAuthModal()
  const paymentsModal = usePaymentsDialog()

  return React.useCallback(async () => {
    const auth = await queryClient
      .fetchQuery({
        queryKey: userAuthQueryKey,
        queryFn: userAuthQueryFetcher,
        ...userAuthQueryOptions,
      })
      .catch(() => null)

    const loggedIn = auth !== null ? isLoggedInQuerySelector(auth) : Boolean(isLoggedIn)
    if (!loggedIn) {
      loginModal.openModal('login')
      return false
    }

    const user = await queryClient
      .fetchQuery({
        queryKey: userQueryKey,
        queryFn: userQueryFetcher,
        ...userQueryOptions,
      })
      .catch(() => null)

    if (!hasAllAccess(user)) {
      paymentsModal.open()
      return false
    }

    return true
  }, [isLoggedIn, loginModal, paymentsModal, queryClient])
}

function hasAllAccess(user: UserQuery | null) {
  return user?.me?.user?.subscriptionAccess.allAccess.isSubscribed === true
}

export function isAllAccessRequiredError(error: unknown) {
  return error instanceof Error && /all access/i.test(error.message)
}
